import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/rbac';
import NotificationBell from './NotificationBell';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();

  // Role-based navigation items
  const getNavigationItems = () => {
    const userRole = profile?.role;

    // Base items for all authenticated users
    const baseItems = [];

    // Lawyer, Partner, Admin items
    if ([ROLES?.LAWYER, ROLES?.PARTNER, ROLES?.ADMIN]?.includes(userRole)) {
      baseItems?.push(
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: 'LayoutDashboard',
          tooltip: 'Practice overview and quick actions'
        },
        {
          label: 'Cases',
          path: '/case-management',
          icon: 'Briefcase',
          tooltip: 'Case management and matter tracking'
        },
        {
          label: 'Clients',
          path: '/client-management',
          icon: 'Users',
          tooltip: 'Client relationship management'
        },
        {
          label: 'Documents',
          path: '/document-management',
          icon: 'FileText',
          tooltip: 'Document storage and collaboration'
        },
        {
          label: 'Time Tracking',
          path: '/time-tracking-dashboard',
          icon: 'Timer',
          tooltip: 'Track billable hours and manage time entries'
        },
        {
          label: 'Time Entries',
          path: '/time-entry-management',
          icon: 'ClipboardList',
          tooltip: 'Manage and review all time tracking records'
        },
        {
          label: 'Rate Management',
          path: '/lawyer-rate-management',
          icon: 'DollarSign',
          tooltip: 'Manage lawyer hourly rates and case overrides'
        },
        {
          label: 'Invoices',
          path: '/invoice-management',
          icon: 'Receipt',
          tooltip: 'Invoice management, billing, and payment tracking'
        },
        {
          label: 'My Profile',
          path: '/lawyer-profile',
          icon: 'User',
          tooltip: 'Manage your professional profile'
        }
      );
    }

    // Video consultation for all roles
    baseItems?.push({
      label: 'Video Consultation',
      path: '/video-consultation',
      icon: 'Video',
      tooltip: 'Secure video conferencing with clients'
    });

    // Client-specific items
    if (userRole === ROLES?.CLIENT) {
      baseItems?.push({
        label: 'Client Portal',
        path: '/client-portal',
        icon: 'Shield',
        tooltip: 'Secure client access environment'
      });
    }

    // Partner and Admin items
    if ([ROLES?.PARTNER, ROLES?.ADMIN]?.includes(userRole)) {
      baseItems?.push({
        label: 'Partner Portal',
        path: '/partner-portal',
        icon: 'Crown',
        tooltip: 'Executive oversight and strategic management'
      });
    }

    // Admin-only items
    if (userRole === ROLES?.ADMIN) {
      baseItems?.push({
        label: 'Admin Dashboard',
        path: '/admin-dashboard',
        icon: 'Shield',
        tooltip: 'Platform administration and system management'
      });
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleKeyDown = (event, path) => {
    if (event?.key === 'Enter' || event?.key === ' ') {
      event?.preventDefault();
      handleNavigation(path);
    }
  };

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className="flex items-center space-x-3 p-4">
      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
        <Icon name="Scale" size={20} color="white" />
      </div>
      {(!isCollapsed || isMobileMenuOpen) && (
        <div className="flex flex-col">
          <span className="text-lg font-heading-semibold text-primary">LawDesk KE</span>
          <span className="text-xs text-text-secondary font-caption-normal">Practice Management</span>
        </div>
      )}
    </div>
  );

  const NavigationItem = ({ item, isMobile = false }) => {
    const isActive = isActiveRoute(item?.path);
    
    return (
      <div
        role="button"
        tabIndex={0}
        className={`
          group relative flex items-center px-4 py-3 mx-2 rounded-interactive cursor-pointer
          transition-smooth min-h-touch-target
          ${isActive 
            ? 'bg-primary-50 text-primary border-l-4 border-primary' :'text-text-secondary hover:bg-secondary-50 hover:text-text-primary'
          }
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
        `}
        onClick={() => handleNavigation(item?.path)}
        onKeyDown={(e) => handleKeyDown(e, item?.path)}
        title={isCollapsed && !isMobile ? item?.tooltip : ''}
      >
        <Icon 
          name={item?.icon} 
          size={20} 
          className={`flex-shrink-0 ${isActive ? 'text-primary' : 'text-current'}`}
        />
        {(!isCollapsed || isMobile) && (
          <span className="ml-3 font-body-medium text-nav-primary">{item?.label}</span>
        )}
        {isCollapsed && !isMobile && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-secondary-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-smooth pointer-events-none whitespace-nowrap z-dropdown">
            {item?.tooltip}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="md:hidden fixed top-4 left-4 z-mobile-menu bg-surface shadow-card rounded-interactive p-2 focus:outline-none focus:ring-2 focus:ring-primary"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-mobile-menu"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      {/* Desktop Sidebar */}
      <aside className={`
        hidden md:flex fixed left-0 top-0 h-full bg-surface border-r border-border z-sidebar
        transition-layout flex-col
        ${isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar'}
      `}>
        <div className="flex items-center justify-between border-b border-border h-header">
          <Logo />
          <div className="flex items-center gap-1 mr-2">
            {!isCollapsed && <NotificationBell />}
            <button
              className="p-2 rounded-interactive hover:bg-secondary-50 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
            </button>
          </div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1">
            {navigationItems?.map((item) => (
              <li key={item?.path}>
                <NavigationItem item={item} />
              </li>
            ))}
          </ul>
        </nav>

        {/* User Profile Section */}
        <div className="mt-auto border-t border-border pt-4">
          {profile && (
            <div className="px-4 py-3 mx-2">
              {(!isCollapsed || isMobileMenuOpen) && (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {profile?.full_name}
                    </p>
                    <p className="text-xs text-text-secondary capitalize">
                      {profile?.role}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div
            role="button"
            tabIndex={0}
            className="group relative flex items-center px-4 py-3 mx-2 rounded-interactive cursor-pointer text-error hover:bg-error-50 transition-smooth min-h-touch-target focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2"
            onClick={async () => {
              try {
                await signOut();
                navigate('/login');
              } catch (error) {
                console.error('Sign out error:', error);
              }
            }}
            onKeyDown={(e) => {
              if (e?.key === 'Enter' || e?.key === ' ') {
                e?.preventDefault();
                signOut()?.then(() => navigate('/login'))?.catch(console.error);
              }
            }}
          >
            <Icon name="LogOut" size={20} className="flex-shrink-0" />
            {(!isCollapsed || isMobileMenuOpen) && (
              <span className="ml-3 font-body-medium">Sign Out</span>
            )}
          </div>
        </div>
      </aside>
      {/* Mobile Sidebar */}
      <aside className={`
        md:hidden fixed left-0 top-0 h-full bg-surface z-mobile-menu w-80 max-w-sm
        transform transition-layout flex flex-col
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between border-b border-border h-header">
          <Logo />
          <div className="flex items-center gap-1 mr-4">
            <NotificationBell />
            <button
              className="p-2 rounded-interactive hover:bg-secondary-50 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close navigation menu"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1">
            {navigationItems?.map((item) => (
              <li key={item?.path}>
                <NavigationItem item={item} isMobile={true} />
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-secondary-200 rounded-full flex items-center justify-center">
              <Icon name="User" size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-body-medium text-text-primary truncate">Legal Professional</p>
              <p className="text-xs text-text-secondary truncate">Advocate & Barrister</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;